import { createPlotConfig } from "../types";

import { changeSwarmBoxPlotOrder } from "./utils/changePlotOrder";
import { createPlot } from "./createPlot";
import * as d3 from "d3";

import {
  selectSwarmBoxSubject,
  unselectSwarmBoxSubject,
} from "./utils/selectSwarmBoxSubject";

import { addLabels, getRenderedAxisLabels } from "./addLabels";
import { applyTickVisibility } from "./utils/tickVisibility";

export const createPlotInfo = ({
  data,
  type,
  scales,
  config = {},
}: createPlotConfig) => {
  scales = scales.filter((scale) => scale);

  const plotCreator = ({
    ssjIdValue,
    selectedIdValue,
    showLegend,
  }: {
    ssjIdValue?: string | null;
    selectedIdValue?: string | null;
    showLegend?: boolean;
  }) =>
    createPlot({
      data,
      type,
      scales,

      config: { ...config, ssjIdValue, selectedIdValue, showLegend },
    });

  const changePlotOrder = (isBoxplotFront: boolean) => {
    if (type === "swarm-box") {
      changeSwarmBoxPlotOrder(config.plotClassName, isBoxplotFront);
    }
  };

  const setSubject = (subject: string, showLegend: boolean) => {
    if (type === "barplot") {
      plotCreator({
        selectedIdValue: subject,
        showLegend,
      });
      return;
    }
    if (type === "swarm-box" || type === "scatter") {
      unselectSwarmBoxSubject(config.plotClassName, config);
      if (subject) {
        selectSwarmBoxSubject(config.plotClassName, subject, config);
      }
    }
  };

  const changeTicksDisplay = ({
    showXTicks,
    showYTicks,
  }: {
    showXTicks: boolean;
    showYTicks: boolean;
  }) => {
    applyTickVisibility(config.plotClassName, { showXTicks, showYTicks });
  };

  const getPreviousLabelContent = (className: string) => {
    const previousTitle = d3.select(`svg.${className}`);

    const previousTitleContent = previousTitle.empty()
      ? ""
      : previousTitle.text();

    return previousTitleContent;
  };

  const updatePlotLabel = (config: PlotConfig, forceUpdate = false) => {
    try {
      const hasLegend = scales.some(
        (scale) => scale.type === "fill" && scale.dataMapping,
      );
      config.showLegend = hasLegend && config.showLegend;

      const { title, subtitle } = config.labels;

      const previousLabels = {
        title: getPreviousLabelContent("title"),
        subtitle: getPreviousLabelContent("subtitle"),
        leftLabel: getPreviousLabelContent("left-axis-name"),
        bottomLabel: getPreviousLabelContent("bottom-axis-name"),
      };
      const renderedAxisLabels = getRenderedAxisLabels(config.labels, config);

      const isLabelUnchanged =
        previousLabels.title === title &&
        previousLabels.subtitle === subtitle &&
        previousLabels.leftLabel === renderedAxisLabels.leftLabel &&
        previousLabels.bottomLabel === renderedAxisLabels.bottomLabel;

      if (isLabelUnchanged && !forceUpdate) return;

      d3.select(`svg.${config.plotClassName}`).selectAll(".labels").remove();
      d3.select(`svg.${config.plotClassName}`)
        .selectAll(".filter-button-svg")
        .remove();

      addLabels(config.labels, config.plotClassName, config);
    } catch (error) {
      console.error("Error updating plot labels:", error);
    }
  };

  return {
    changeTicksDisplay,
    plotCreator,
    setSubject,
    changePlotOrder,
    updatePlotLabel,
    config,
    scales,
  };
};
